import React from 'react';
import {
  Box,
  Typography,
  Badge,
  styled,
  alpha,
  Button,
  useTheme
} from '@mui/material';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Lead, User } from '../types';
import { AgentUserRS } from '../utils/api';
import SortableLeadCard from './SortableLeadCard';
import { useTranslation } from 'react-i18next';

const ColumnContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOver' && prop !== 'columnId' && prop !== 'statusColor'
})<{ isOver?: boolean; columnId: string; statusColor?: string }>(({ theme, isOver, columnId, statusColor }) => {
  const getGradient = () => {
    // Use dynamic status color if available
    if (statusColor) {
      const color = statusColor;
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);

      return theme.palette.mode === 'dark'
        ? `linear-gradient(135deg, rgba(${r},${g},${b},0.06) 0%, rgba(255,255,255,0.02) 100%)`
        : `linear-gradient(135deg, rgba(${r},${g},${b},0.08) 0%, #FFFFFF 100%)`;
    }

    // Fallback to hardcoded colors for backward compatibility
    switch (columnId) {
      case 'novo':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(255,255,255,0.02) 100%)'
          : 'linear-gradient(135deg, #E3F2FD 0%, #FFFFFF 100%)';
      case 'contato_inicial':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(255,255,255,0.02) 100%)'
          : 'linear-gradient(135deg, #FFF3E0 0%, #FFFFFF 100%)';
      case 'qualificado':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(255,255,255,0.02) 100%)'
          : 'linear-gradient(135deg, #E8F5E9 0%, #FFFFFF 100%)';
      case 'proposta':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(147,51,234,0.06) 0%, rgba(255,255,255,0.02) 100%)'
          : 'linear-gradient(135deg, #F3E5F5 0%, #FFFFFF 100%)';
      case 'negociacao':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(239,68,68,0.06) 0%, rgba(255,255,255,0.02) 100%)'
          : 'linear-gradient(135deg, #FBE9E7 0%, #FFFFFF 100%)';
      case 'fechado':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(255,255,255,0.02) 100%)'
          : 'linear-gradient(135deg, #E8F5E9 0%, #FFFFFF 100%)';
      case 'perdido':
        return theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, rgba(248,113,113,0.06) 0%, rgba(255,255,255,0.02) 100%)'
          : 'linear-gradient(135deg, #FFEBEE 0%, #FFFFFF 100%)';
      default:
        return theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.02)
          : 'linear-gradient(135deg, #F5F5F5 0%, #FFFFFF 100%)';
    }
  };

  const getColumnAccentColor = () => {
    switch (columnId) {
      case 'novo':
        return '#0288d1';
      case 'contato_inicial':
        return '#f06e65';
      case 'qualificado':
        return '#35e87c';
      case 'proposta':
        return '#7b1fa2';
      case 'negociacao':
        return '#d32f2f';
      case 'fechado':
        return '#35e87c';
      case 'perdido':
        return '#b71c1c';
      default:
        return '#757575';
    }
  };

  return {
    padding: theme.spacing(2),
    minHeight: 500,
    width: 350,
    minWidth: 350,
    flexShrink: 0,
    background: isOver ? alpha(theme.palette.primary.main, 0.06) : getGradient(),
    borderRadius: 20,
    border: isOver ? 'none' : '2px solid transparent',
    boxShadow: isOver
      ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.25)}`
      : (theme.palette.mode === 'dark' ? '0 2px 12px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.08)'),
    '&:hover': {
      boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
      transform: 'translateY(-2px)',
    },
    position: 'relative',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: isOver ? 'scale(1.01)' : 'scale(1)',
    overflow: 'hidden',

    // Efeito pontilhado na borda quando está recebendo um card
    ...(isOver && {
      border: `2px dashed ${getColumnAccentColor()}`,
      borderWidth: '3px',
      animation: 'dashedPulse 1.5s ease-in-out infinite alternate',
      '@keyframes dashedPulse': {
        '0%': {
          borderColor: getColumnAccentColor(),
          transform: 'scale(1)',
        },
        '100%': {
          borderColor: alpha(getColumnAccentColor(), 0.6),
          transform: 'scale(1.01)',
        },
      },
    }),

    // Efeito de fundo circular decorativo
    '& .decorative-bg': {
      content: '""',
      position: 'absolute',
      bottom: -100,
      right: -100,
      width: 200,
      height: 200,
      borderRadius: '50%',
      background: theme.palette.gradient?.primary || 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)',
      opacity: theme.palette.mode === 'dark' ? 0.06 : 0.03,
      transition: 'transform 0.5s ease',
      transform: isOver ? 'scale(1.5)' : 'scale(1)',
      zIndex: -2,
    },

    [theme.breakpoints.down('md')]: {
      width: 260,
      minWidth: 260,
      padding: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      minHeight: 400,
      padding: theme.spacing(2),
      flexShrink: 1,
    },
  };
});

const ColumnHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.08)}`,
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: 60,
    height: 2,
    background: 'linear-gradient(90deg, #1a1a1a 0%, #1a1a1a 100%)',
  },
}));

const ColumnTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'accentColor'
})<{ accentColor: string }>(({ theme, accentColor }) => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  background: `linear-gradient(135deg, ${accentColor} 0%, ${alpha(accentColor, 0.7)} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  letterSpacing: '-0.025em',

  [theme.breakpoints.down('sm')]: {
    fontSize: '1.1rem',
  },
}));

const StyledBadge = styled(Badge, {
  shouldForwardProp: (prop) => prop !== 'accentColor'
})<{ accentColor: string }>(({ theme, accentColor }) => ({
  marginRight: theme.spacing(1),
  '& .MuiBadge-badge': {
    background: `linear-gradient(135deg, ${accentColor} 0%, ${alpha(accentColor, 0.8)} 100%)`,
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '0.875rem',
    height: 28,
    minWidth: 28,
    borderRadius: 14,
    boxShadow: `0 2px 8px ${alpha(accentColor, 0.3)}`,
  },
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  minHeight: 300,
  // Use viewport height to better accommodate long columns without breaking page scroll
  maxHeight: '65vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  // Keep scrollbars from shifting layout when they appear
  scrollbarGutter: 'stable',
  // Prevent parent/page from scrolling when reaching edges inside this container
  overscrollBehavior: 'contain',
  paddingRight: theme.spacing(1),
  // Adiciona padding no fundo para garantir espaço para drop mesmo com muitos cards
  paddingBottom: theme.spacing(15),
  position: 'relative',
  zIndex: 1,
  // Avoid scroll jump when list changes by disabling overflow anchoring on children
  '& > *': {
    overflowAnchor: 'none',
    // Allow pointer events to pass through to detect column hover
    pointerEvents: 'auto',
  },

  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.primary.main, 0.05),
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)',
    borderRadius: 3,

    '&:hover': {
      background: 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)',
    },
  },

  [theme.breakpoints.down('md')]: {
    minHeight: 260,
    maxHeight: '60vh',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: 220,
    maxHeight: '55vh',
  },
}));

// Overlay invisível para garantir detecção de drop em toda área
const DropOverlay = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOver'
})<{ isOver?: boolean }>(({ theme, isOver }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  pointerEvents: isOver ? 'none' : 'auto',
  zIndex: 0,
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: 200,
  padding: theme.spacing(3),
  borderRadius: 16,
  border: `2px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.02),
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  textAlign: 'center',
  transition: 'all 0.3s ease',

  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },

  '& svg': {
    fontSize: 48,
    marginBottom: theme.spacing(2),
    opacity: 0.3,
  },
}));

interface StyledKanbanColumnProps {
  id: string;
  title: string;
  leads: Lead[];
  users: User[];
  agentUsers?: AgentUserRS[];
  onViewDetails: (lead: Lead) => void;
  onAssign?: (lead: Lead) => void;
  onTransfer?: (lead: Lead) => void;
  isMobile?: boolean;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
  loadingMore?: boolean;
  statusColor?: string;
}

const StyledKanbanColumn: React.FC<StyledKanbanColumnProps> = ({
  id,
  title,
  leads,
  users,
  agentUsers = [],
  onViewDetails,
  onAssign,
  onTransfer,
  isMobile = false,
  onLoadMore,
  canLoadMore,
  loadingMore,
  statusColor,
}) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  const theme = useTheme();
  const { t } = useTranslation();

  // Function to translate status name
  const translateStatus = (statusName: string, statusId: string): string => {
    // Try to translate by ID first (most reliable)
    const translationKey = `dashboard.kanban.statusLabels.${statusId}`;
    const translated = t(translationKey);
    
    // If translation exists and is different from the key, return it
    if (translated !== translationKey) {
      return translated;
    }
    
    // Fallback: try to translate by normalized name
    const normalizedName = statusName.toLowerCase().replace(/\s+/g, '_');
    const nameTranslationKey = `dashboard.kanban.statusLabels.${normalizedName}`;
    const nameTranslated = t(nameTranslationKey);
    
    if (nameTranslated !== nameTranslationKey) {
      return nameTranslated;
    }
    
    // If no translation found, return original name
    return statusName;
  };

  const getColumnAccentColor = (columnId: string) => {
    // Use the passed statusColor if available, otherwise fall back to hardcoded colors
    if (statusColor) {
      return statusColor;
    }

    switch (columnId) {
      case 'novo':
        return '#0288d1';
      case 'contato_inicial':
        return '#f06e65';
      case 'qualificado':
        return '#35e87c';
      case 'proposta':
        return '#7b1fa2';
      case 'negociacao':
        return '#d32f2f';
      case 'fechado':
        return '#35e87c';
      case 'perdido':
        return '#b71c1c';
      default:
        return '#757575';
    }
  };

  const accentColor = getColumnAccentColor(id);

  return (
    <ColumnContainer
      ref={setNodeRef}
      isOver={isOver}
      columnId={id}
      statusColor={statusColor}
    >
      <DropOverlay isOver={isOver} />
      <Box className="decorative-bg" />
      <ColumnHeader>
        <ColumnTitle accentColor={accentColor} variant="h6">
          {translateStatus(title, id)}
        </ColumnTitle>
        <StyledBadge
          badgeContent={leads.length}
          accentColor={accentColor}
          max={99}
        />
      </ColumnHeader>

      <ScrollableContent
        data-column-scroll={id}
        onScroll={(e) => {
          if (!onLoadMore || !canLoadMore || loadingMore) return;
          const target = e.currentTarget as HTMLElement;
          const threshold = 120;
          const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - threshold;
          if (nearBottom) {
            onLoadMore();
          }
        }}
      >
        <SortableContext
          items={leads.map(lead => lead._id.$oid)}
          strategy={verticalListSortingStrategy}
        >
          {leads.length > 0 ? (
            leads.map((lead) => (
              <SortableLeadCard
                key={lead._id.$oid}
                lead={lead}
                onViewDetails={onViewDetails}
                users={users}
                agentUsers={agentUsers}
                onAssign={onAssign}
                onTransfer={onTransfer}
                isMobile={isMobile}
              />
            ))
          ) : (
            <EmptyState>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
              </svg>
              <Typography variant="body2" color="inherit">
                {t('dashboard.kanban.noLeadsInStage')}
              </Typography>
              <Typography variant="caption" color="inherit" sx={{ mt: 1, opacity: 0.7 }}>
                {t('dashboard.kanban.dragLeadsHere')}
              </Typography>
            </EmptyState>
          )}
        </SortableContext>
        {(onLoadMore || canLoadMore === false) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="contained"
              sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
              size="small"
              disabled={loadingMore || !canLoadMore}
              onClick={onLoadMore || (() => {})}
            >
              {loadingMore ? t('dashboard.kanban.loading') : (canLoadMore ? t('dashboard.kanban.loadMore') : t('dashboard.kanban.noMoreResults'))}
            </Button>
          </Box>
        )}
      </ScrollableContent>
    </ColumnContainer>
  );
};

export default StyledKanbanColumn;